import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
} from '@angular/core';
import {
    IEntity,
    IManageEntityFacade,
    ISearchFacade,
    ISearchFields,
    PermissionTypes,
    PFEntities,
    PlatformModules,
    SearchStrategy,
    TableAction,
    TableColumn,
} from '@pf/shared-common';
import { EntityNotificationService } from '../services/entity-notification.service';

@UntilDestroy()
@Component({
    selector: 'pf-entity-base-manage',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export abstract class EntityBaseManageComponent<
    TEntity extends object & IEntity,
    TTableEntity extends object & IEntity = TEntity,
> implements OnInit
{
    protected readonly entityNotifications = inject(EntityNotificationService);

    abstract title: string;
    abstract tableWidth: string;
    abstract searchFields: ISearchFields<TTableEntity>;
    abstract searchStrategy?: SearchStrategy;
    abstract entityType: string;
    abstract entityName: string;
    abstract entityFacade: IManageEntityFacade<TEntity> &
        ISearchFacade<TEntity>;
    abstract permissionPlatform: PlatformModules;
    abstract permissionEntity: PFEntities;
    abstract permissionType: PermissionTypes;

    columns: TableColumn<TTableEntity>[] = [];
    actions: TableAction<TTableEntity>[] = [];
    canUndoDelete = true;

    ngOnInit(): void {
        this.columns = this.setColumns();
        this.actions = this.setActions();
    }

    abstract setColumns(): TableColumn<TTableEntity>[];

    abstract setActions(): TableAction<TTableEntity>[];

    add() {
        this.entityFacade.add();
    }

    edit(entity: TEntity | string) {
        this.entityFacade.edit(typeof entity === 'string' ? entity : entity.id);
    }

    delete(entity: TEntity | string) {
        this.entityFacade
            .delete$(typeof entity === 'string' ? entity : entity.id)
            .subscribe();
    }

    restore(entity: TEntity | string) {
        this.entityFacade
            .undoDelete$(typeof entity === 'string' ? entity : entity.id)
            .subscribe();
    }

    protected registerManageEntityNotifications() {
        this.registerNotificationsForEntity(
            this.entityFacade,
            this.entityName,
            this.canUndoDelete
        );
    }

    protected registerNotificationsForEntity<
        TManagedEntity extends object & IEntity,
    >(
        entityFacade: IManageEntityFacade<TManagedEntity> &
            ISearchFacade<TManagedEntity>,
        entityName: string,
        canUndoDelete: boolean
    ) {
        this.entityNotifications.notifyOnEntityAdded(
            entityFacade,
            entityName,
            untilDestroyed(this)
        );
        this.entityNotifications.notifyOnEntityUpdated(
            entityFacade,
            entityName,
            untilDestroyed(this)
        );
        this.entityNotifications.notifyOnEntityDeleted(
            entityFacade,
            entityName,
            canUndoDelete,
            untilDestroyed(this)
        );
        this.entityNotifications.notifyOnEntityRestored(
            entityFacade,
            entityName,
            untilDestroyed(this)
        );
    }
}
